import {
  Checkbox,
  Dialog,
  Popover,
  Tooltip,
} from "@mui/material";
import {
  ArrowLeft,
  Edit,
  PlusCircle,
  Trash,
  Trash2,
} from "feather-icons-react/build/IconComponents";
import { t } from "i18next";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import API from "../../API/newIndex";
import BulkEditFaq from "./BulkEditFaq";
import { DataLoad, PlanPermission } from "../../App";
import Breadcrumbs from "../../controllers/breadCrumbs";
import { formatDate } from "../../controllers/FormatAmount";
import { getInitialPage, replacePageInUrl } from "../../controllers/PageHandleFromQuery";
import { parentNavigate } from "../../controllers/ParentNavigate";
import { TableColumnsContext } from "../../controllers/TableColumnsContext";
import { useUser } from "../../controllers/UserContext";
import NoRecords from "../Main/NoRecords";
import Notification from "../Notification";
import {
  ButtonComponent,
  CommonTable,
  ConfirmationPopup,
  PaginationComponent,
  SearchComponent,
  PageLimitDropDownComponent,
  TableStatusDropdown,
} from "@shopeasy-dashboard-ui/ui-library";
import { isAscendingOrder } from "../../utilities/staticFunction";
import * as defaultCustomFields from '../../utilities/defaultCustomTableField';
import useTableApi from "../../utilities/useTableApi";

const PathCell = ({ path }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const paths = Array.isArray(path)
    ? path
      .map((item) => (typeof item === "object" ? item?.name || item?.link || "" : String(item)))
      .filter(Boolean)
    : path
      ? [typeof path === "object" ? path?.name || path?.link || "" : String(path)].filter(Boolean)
      : [];

  if (paths.length === 0) {
    return <span className="text-xs table-text">-</span>;
  }

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const visibleCount = paths.length === 1 ? 1 : 2;
  const visiblePaths = paths.slice(0, visibleCount);
  const remainingCount = paths.length - visibleCount;

  return (
    <div className="flex flex-col items-start gap-1 py-1 max-w-full">
      {/* Top row with primary path pills */}
      <div className="flex items-center gap-1.5 flex-wrap max-w-full">
        {visiblePaths.map((p, idx) => (
          <span
            key={idx}
            className="inline-block truncate max-w-[130px] whitespace-nowrap text-[12px] px-3 py-0.5 rounded-full font-normal align-middle cursor-default"
            style={{
              backgroundColor: "var(--focus-tab-color)",
              borderColor: "var(--border-color)",
              borderWidth: "1px",
              borderStyle: "solid",
              color: "var(--primary-text-color)",
            }}
            title={p}
          >
            {p}
          </span>
        ))}
      </div>

      {/* Bottom row with +N more trigger pill */}
      {
        remainingCount > 0 && (
          <div
            onMouseEnter={handlePopoverOpen}
            onMouseLeave={handlePopoverClose}
            className="inline-flex mt-0.5"
          >
            <span
              className="inline-flex items-center whitespace-nowrap text-[11px] px-2.5 py-0.5 rounded-full font-medium transition-all cursor-pointer select-none"
              style={{
                backgroundColor: "var(--focus-tab-color)",
                borderColor: "var(--border-color)",
                borderWidth: "1px",
                borderStyle: "solid",
                color: "var(--primary-text-color)",
              }}
            >
              +{remainingCount} more
            </span>

            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handlePopoverClose}
              disableRestoreFocus
              sx={{
                pointerEvents: "none",
              }}
              anchorOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
              slotProps={{
                paper: {
                  className: "overflow-visible !bg-transparent !shadow-none !border-none",
                  style: {
                    marginBottom: "10px",
                    pointerEvents: "auto",
                    overflow: "visible",
                  },
                  onMouseEnter: () => setAnchorEl(anchorEl),
                  onMouseLeave: handlePopoverClose,
                },
              }}
            >
              <div
                className="relative w-[340px] max-w-[380px] p-3.5 rounded-[12px]"
                style={{
                  backgroundColor: "var(--secondary-bg-color)",
                  borderColor: "var(--border-color)",
                  borderWidth: "1px",
                  borderStyle: "solid",
                  boxShadow: "var(--shadow-popover, 0 12px 36px rgba(0,0,0,0.3))",
                }}
              >
                {/* Header */}
                <div
                  className="flex items-center justify-between pb-2.5 mb-2.5"
                  style={{
                    borderBottom: "1px solid var(--border-color)",
                  }}
                >
                  <span
                    className="text-[13px] font-semibold"
                    style={{ color: "var(--primary-text-color)" }}
                  >
                    Selected Paths
                  </span>
                  <span
                    className="text-[11px] font-medium px-2 py-0.5 rounded-[4px]"
                    style={{
                      backgroundColor: "var(--focus-tab-color)",
                      borderColor: "var(--border-color)",
                      borderWidth: "1px",
                      borderStyle: "solid",
                      color: "var(--table-text-color)",
                    }}
                  >
                    {paths.length}
                  </span>
                </div>

                {/* Items List */}
                <div className="flex flex-wrap gap-2 overflow-y-auto max-h-[220px] pe-1 custom-scrollbar">
                  {paths.map((p, idx) => (
                    <span
                      key={idx}
                      className="text-[12px] px-3 py-1.5 rounded-[6px] font-normal leading-tight"
                      style={{
                        backgroundColor: "var(--focus-tab-color)",
                        borderColor: "var(--border-color)",
                        borderWidth: "1px",
                        borderStyle: "solid",
                        color: "var(--primary-text-color)",
                      }}
                    >
                      {p}
                    </span>
                  ))}
                </div>

                {/* Center Pointer Arrow */}
                <div
                  className="absolute -bottom-[6px] left-1/2 -translate-x-1/2 w-[12px] h-[12px] rotate-45"
                  style={{
                    backgroundColor: "var(--secondary-bg-color)",
                    borderRight: "1px solid var(--border-color)",
                    borderBottom: "1px solid var(--border-color)",
                  }}
                />
              </div>
            </Popover>
          </div>
        )
      }
    </div >
  );
};

function Faqs() {
  const handleTableApi = useTableApi();
  const location = useLocation();
  const { setPlanPermission } = useContext(PlanPermission);
  const [isNotification, setIsNotification] = useState(false);
  const [notificationMsg, setNotificationMsg] = useState("");
  const [severity, setSeverity] = useState();
  const initialLimit = localStorage.getItem("limit") || 10;
  const [limit, setLimit] = useState(initialLimit);
  const [faqFilter, setFaqFilter] = useState("");
  const { dataLoading, setDataLoading } = useContext(DataLoad);
  const [search, setSearch] = useState(
    location?.state?.searchkey ? location?.state?.searchkey : "",
  );

  const { sortData, isUserLoaded } = useUser();
  const userContext = useUser();

  const savedSort = sortData?.faqs || sortData?.faq;

  const [sortBy, setSortBy] = useState(
    () => (location?.state?.accending ? location?.state?.field : savedSort?.sort_by || "createdAt")
  );
  const [orderBy, setOrderBy] = useState(() => {
    if (location?.state?.accending) {
      return { orderByValue: "ASC", isDecending: false };
    }
    const isAsc = isAscendingOrder(savedSort?.order_by);
    return {
      orderByValue: savedSort?.order_by ? (isAsc ? "ASC" : "DESC") : "DESC",
      isDecending: savedSort?.order_by ? !isAsc : true,
    };
  });
  const isInitialSortLoaded = useRef(Boolean(location?.state?.field || savedSort?.sort_by));

  const [tableData, setTableData] = useState();
  const [searchedProductID, setSearchedProductID] = useState("");
  const [clearRow, setClearRow] = useState(false);

  const { tableColumnsData } = useContext(TableColumnsContext);

  const apiFieldNames = useMemo(() => {
    const dataObj = tableColumnsData?.data?.data || tableColumnsData?.data || tableColumnsData;
    if (Array.isArray(dataObj?.faqs)) return dataObj.faqs;
    if (Array.isArray(dataObj?.faq)) return dataObj.faq;
    if (Array.isArray(tableColumnsData?.faqs)) return tableColumnsData.faqs;
    if (Array.isArray(tableColumnsData?.faq)) return tableColumnsData.faq;
    return [];
  }, [tableColumnsData]);

  const dynamicColumns = useMemo(() => {
    const fixedKeys = new Set([
      "_id",
      "__v",
      "client",
      "tenant",
      "store_client",
      "store_tenant",
      "user",
      "question",
      "answer",
      "faq_type",
      "path",
      "product",
      "category",
      "blog",
      "createdAt",
      "updatedAt",
      "action",
      "actions",
      "checkbox",
      "selection",
      "select",
      "drag_handle",
      "draghandle",
      "drag",
    ]);

    return apiFieldNames
      .filter((fieldName) => typeof fieldName === "string" && fieldName.trim() && !fixedKeys.has(fieldName))
      .map((fieldName) => {
        const isDotted = fieldName.includes(".");
        const parts = isDotted ? fieldName.split(".") : null;

        const label = fieldName
          .replace(/\./g, " • ")
          .replace(/[-_]/g, " ")
          .replace(/([a-z])([A-Z])/g, "$1 $2")
          .replace(/\b\w/g, (c) => c.toUpperCase());

        const getValue = (row) => {
          if (!row) return undefined;
          if (isDotted && parts) {
            return parts.reduce((obj, k) => (obj != null ? obj[k] : undefined), row);
          }
          return row[fieldName];
        };

        return {
          key: fieldName,
          title: label,
          width: "160px",
          minWidth: "110px",
          responsiveWidth: "100px",
          headerClassName: "px-3 whitespace-nowrap truncate",
          cellClassName: "px-3 truncate",
          render: (row) => {
            const val = getValue(row);
            if (val === null || val === undefined || val === "") return "-";
            if (typeof val === "boolean") return val ? "Yes" : "No";
            if (fieldName.toLowerCase().includes("date") && typeof val === "string") {
              return formatDate(val);
            }
            if (Array.isArray(val)) {
              return val
                .map((item) => (typeof item === "object" ? item?.name || item?.title || item?.link || JSON.stringify(item) : item))
                .join(", ");
            }
            if (typeof val === "object") {
              return val.name || val.title || val.link || JSON.stringify(val);
            }
            return String(val);
          },
        };
      });
  }, [apiFieldNames]);

  useEffect(() => {
    setSearch(location?.state?.searchkey ? location?.state?.searchkey : "");
  }, [location?.state?.searchkey]);

  useEffect(() => {
    if (!isInitialSortLoaded.current && isUserLoaded) {
      isInitialSortLoaded.current = true;
      if (savedSort && savedSort.sort_by) {
        const isAsc = isAscendingOrder(savedSort.order_by);
        const targetOrderBy = isAsc ? "ASC" : "DESC";
        if (savedSort.sort_by !== sortBy || targetOrderBy !== orderBy.orderByValue) {
          setSortBy(savedSort.sort_by);
          setOrderBy({
            orderByValue: targetOrderBy,
            isDecending: !isAsc,
          });
        }
      }
    }
  }, [savedSort, isUserLoaded, sortBy, orderBy.orderByValue]);

  // Sorting Data
  const SortData = (sort_by, explicitOrder) => {
    setSortBy(sort_by);
    let nextOrder = "DESC";
    if (explicitOrder !== undefined) {
      nextOrder = isAscendingOrder(explicitOrder) ? "ASC" : "DESC";
    } else if (sortBy === sort_by) {
      const isCurrentlyAsc = isAscendingOrder(orderBy);
      nextOrder = isCurrentlyAsc ? "DESC" : "ASC";
    } else {
      nextOrder = "DESC";
    }
    setOrderBy({
      orderByValue: nextOrder,
      isDecending: nextOrder === "DESC",
    });
  };

  // paginate
  const [Page, setPage] = useState(getInitialPage());
  const [selectedRowsId, setSelectedRowsId] = useState([]);
  const [openBulkEditModal, setOpenBulkEditModal] = useState(false);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    replacePageInUrl(Page);
  }, [Page]);

  var startRecord = tableData?.currentPage ? (tableData?.currentPage - 1) * limit + 1 : 0;
  var endRecord = tableData?.currentPage ? Math.min(tableData?.currentPage * limit, tableData?.totalRecords) : 0;

  const debounceTimeout = useRef(null);

  useEffect(() => {
    if (!isUserLoaded && !isInitialSortLoaded.current && !location?.state?.field) {
      return;
    }

    const trimmed = search?.trim() || "";
    clearTimeout(debounceTimeout.current);
    if (trimmed === "") {
      GetAllFaqs();
      setClearRow((prev) => !prev);
    } else if (trimmed.length >= 3) {
      debounceTimeout.current = setTimeout(() => {
        GetAllFaqs();
        setClearRow((prev) => !prev);
      }, 500);
    }
    return () => clearTimeout(debounceTimeout.current);
  }, [Page, limit, search, faqFilter, sortBy, orderBy, isUserLoaded]);

  const GetAllFaqs = async () => {
    setDataLoading(true);
    try {
      const data = await API({
        url: `/admin/faq/getFaqs?page=${Page}&limit=${limit}&searchkey=${search?.length > 2 ? search : ""
          }&faq_type=${faqFilter}&sort_by=${sortBy}&order_by=${orderBy.orderByValue}`,
        method: "get",
      });
      if (data.status === 200 || data.status === 304) {
        const result = data?.data;
        setTableData(result?.data);
      }
    } catch (error) {
      if (error?.response?.status === 882) {
        setPlanPermission({ isPlanPermissions: false, forModule: "faq" });
      }
      setIsNotification(true);
      setNotificationMsg(error);
      setSeverity("error");
    } finally {
      setDataLoading(false);
    }
  };

  const handleSelectRow = (e, id) => {
    if (e?.nativeEvent?.shiftKey) {
      const prevIndex = tableData?.records?.findIndex(
        (x) => x._id === selectedRowsId[selectedRowsId?.length - 1],
      );
      const currIndex = tableData?.records?.findIndex((x) => x._id === id);
      const startIndex = Math.min(prevIndex, currIndex);
      const endIndex = Math.max(prevIndex, currIndex);
      const shiftSelected = tableData?.records?.slice(startIndex, endIndex + 1);

      if (selectedRowsId?.includes(id)) {
        setSelectedRowsId((prev) => [
          ...new Set(prev.filter((x) => !shiftSelected.find((item) => item._id === x))),
        ]);
      } else {
        setSelectedRowsId((prev) => [
          ...new Set([...prev, ...shiftSelected.map((item) => item._id)]),
        ]);
      }
    } else if (id === "all") {
      if (selectedRowsId?.length > 0) {
        setSelectedRowsId([]);
      } else {
        setSelectedRowsId(tableData?.records?.map((x) => x?._id) || []);
      }
    } else {
      const existId = selectedRowsId?.includes(id);
      if (!existId) {
        setSelectedRowsId((prev) => [...prev, id]);
      } else {
        setSelectedRowsId((prev) => prev.filter((x) => x !== id));
      }
    }
  };

  // Api call For delete FAQ
  const [confirmationPopup, setConfirmationPopup] = useState(false);

  const HandleDelete = async () => {
    try {
      const payload = {
        id: selectedRowsId,
      };
      const data = await API({ url: `/admin/faq/daleteFaq`, method: "post", data: payload });
      if (data.status === 200 || data.status === 304) {
        setIsNotification(true);
        setNotificationMsg("Removed Successfully");
        setSeverity("success");
        setSelectedRowsId([]);
        setClearRow(!clearRow);
        GetAllFaqs();
      }
    } catch (error) {
      setIsNotification(true);
      setNotificationMsg(error);
      setSeverity("error");
    }
    setConfirmationPopup(false);
  };

  const handleCloseConfirm = (value) => {
    setConfirmationPopup(value);
  };

  const handleClose = () => {
    setIsNotification(false);
    setNotificationMsg("");
    setSeverity("");
  };

  // Edit Request
  const Handleedit = (elem) => {
    if ((faqFilter === "product" || faqFilter === "category") && searchedProductID !== "") {
      parentNavigate(`/faqs/${elem._id}/true/${searchedProductID}`);
    } else {
      parentNavigate(`/faqs/${elem._id}/false/null`);
    }
  };

  // Define Table Columns for CommonTable
  const columns = [
    {
      key: "checkbox",
      width: "40px",
      minWidth: "40px",
      maxWidth: "40px",
      responsiveWidth: "40px",
      headerClassName: "!px-0 w-[40px] min-w-[40px] max-w-[40px] text-center",
      cellClassName: "!px-0 w-[40px] min-w-[40px] max-w-[40px] text-center",
      align: "text-center",
      ignoreRowClick: true,
      title: (
        <Checkbox
          checked={tableData?.records?.length > 0 && selectedRowsId?.length === tableData?.records?.length}
          indeterminate={
            selectedRowsId?.length > 0 && selectedRowsId?.length < tableData?.records?.length
          }
          onChange={(e) => handleSelectRow(e, "all")}
        />
      ),
      render: (row) => (
        <Checkbox
          checked={selectedRowsId?.length > 0 && selectedRowsId?.includes(row?._id)}
          onChange={(e) => handleSelectRow(e, row?._id)}
        />
      ),
    },
    {
      key: "question",
      title: t("lang_Question") || "Question",
      width: "450px",
      maxWidth: "550px",
      headerClassName: "px-3",
      cellClassName: "px-3",
      render: (row) => (
        <p className="line-clamp-2 text-xs text-[var(--primary-text-color)] font-medium">
          {row.question || "-"}
        </p>
      ),
    },
    {
      key: "answer",
      title: t("lang_Answer") || "Answer",
      width: "500px",
      maxWidth: "550px",
      headerClassName: "px-3",
      cellClassName: "px-3",
      render: (row) => (
        <span className="line-clamp-2 table-text text-xs">
          {row.answer
            ? row.answer.length <= 80
              ? row.answer.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()
              : row.answer.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().substring(0, 80) + "..."
            : "-"}
        </span>
      ),
    },
    {
      key: "faq_type",
      title: t("lang_type") || "Type",
      width: "120px",
      minWidth: "100px",
      headerClassName: "px-3",
      cellClassName: "px-3",
      render: (row) => (
        <Tooltip title={row?.faq_type || ""} arrow placement="top">
          <span
            className="bg-[var(--focus-tab-color)] border border-[var(--border-color)] inline-block truncate max-w-full whitespace-nowrap text-[12px] px-2.5 py-0.5 rounded-[6px] font-medium text-[var(--primary-text-color)] capitalize align-middle cursor-default"
          >
            {row?.faq_type || "-"}
          </span>
        </Tooltip>
      ),
    },
    {
      key: "path",
      title: t("lang_path") || "Path",
      width: "240px",
      minWidth: "160px",
      headerClassName: "px-3",
      cellClassName: "px-3",
      render: (row) => <PathCell path={row?.path} />,
    },
    {
      key: "createdAt",
      title: t("lang_created_date") || "Created Date",
      width: "150px",
      minWidth: "140px",
      headerClassName: "px-3 whitespace-nowrap",
      cellClassName: "px-3 whitespace-nowrap",
      render: (row) => (
        <span className="table-text text-xs whitespace-nowrap">
          {row?.createdAt ? formatDate(row.createdAt) : "-"}
        </span>
      ),
    },
    ...dynamicColumns,
    {
      key: "action",
      title: t("lang_action") || "Action",
      width: "100px",
      minWidth: "80px",
      maxWidth: "110px",
      responsiveWidth: "110px",
      headerClassName: "px-3 text-right",
      cellClassName: "px-3 text-right",
      align: "text-right",
      ignoreRowClick: true,
      render: (row) => (
        <div className="flex items-center space-x-2.5 justify-end w-full">
          <Tooltip title="Edit" arrow>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                Handleedit(row);
              }}
            >
              <Edit className="w-[16px] h-[16px] text-[var(--table-text-color)] hover:text-[var(--primary-text-color)] transition-colors" />
            </button>
          </Tooltip>
          <Tooltip title="Delete" arrow>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setConfirmationPopup(true);
                setSelectedRowsId([row?._id]);
              }}
            >
              <Trash className="w-[16px] h-[16px] text-red-500 hover:text-red-600 transition-colors" />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  // Table header top filter bar (matching Blog app)
  const tableHeaderFilter = (manageFieldsBtn) => {
    return (
      <div className="relative w-full border-b main-border-color secondary-bg-color px-3 py-2 flex flex-col pe-3 min-h-[50px]">
        <div className="flex flex-wrap items-center justify-between w-full gap-2 min-h-[38px]">
          <div className="flex items-center">
            <TableStatusDropdown
              value={faqFilter}
              onChange={(val) => {
                setFaqFilter(val);
                setPage(1);
              }}
              options={[
                { label: t("lang_all"), value: "", count: tableData?.totalRecords },
                { label: t("lang_pages"), value: "pages" },
                { label: t("lang_product"), value: "product" },
                { label: t("lang_category"), value: "category" },
                { label: t("lang_blog"), value: "blog" },
              ]}
              placeholder={t("lang_all")}
            />
          </div>

          {/* Search bar & Limit select on right */}
          <div className="hidden md:flex items-center gap-2 ml-auto">
            <div className="w-[180px] md:w-[200px]">
              <SearchComponent
                placeholder="Search FAQs"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                onClear={() => {
                  setSearch("");
                  setPage(1);
                }}
                isClearable={true}
                size="sm"
                iconPlacement="left"
              />
            </div>

            <div className="w-[80px] md:w-[100px] shrink-0 select-sm-height select-hide-label select-no-padding-y">
              <PageLimitDropDownComponent
                limit={limit}
                setLimit={setLimit}
                setPage={setPage}
                totalRecords={tableData?.totalRecords || 0}
              />
            </div>
            {manageFieldsBtn}
          </div>
        </div>

        {/* Selected rows bulk actions */}
        {selectedRowsId?.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-2 pt-2 border-t main-border-color w-full">
            <span className="text-xs px-2 whitespace-nowrap font-medium">
              {selectedRowsId?.length} {t("lang_selected")}
            </span>
            <span className="text-gray-300">|</span>
            <ButtonComponent
              variant="outline"
              size="sm"
              className="mx-1"
              onClick={() => setOpenBulkEditModal(true)}
              icon={<Edit className="w-3.5 h-3.5" />}
            >
              {t("lang_bulk_edit", "Bulk Edit")}
            </ButtonComponent>
            <ButtonComponent
              variant="danger_outline"
              size="sm"
              icon={<Trash2 className="w-3.5 h-3.5" />}
              onClick={(e) => {
                e.preventDefault();
                setConfirmationPopup(true);
              }}
            >
              {t("lang_delete_selection")}
            </ButtonComponent>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <ConfirmationPopup
        heading="Delete FAQ"
        subheading="Do you really want to delete this FAQ from your store?"
        confirmationPopup={confirmationPopup}
        handleCloseConfirm={handleCloseConfirm}
        runFunction={HandleDelete}
      />
      {isNotification && notificationMsg && (
        <Notification message={notificationMsg} close={handleClose} severity={severity} />
      )}
      <div className="h-auto overflow-y-auto overflow-x-hidden pb-[64px] p-5">
        <div className="flex flex-wrap items-end justify-between w-full page-header-pb">
          <div className="flex items-center space-x-2">
            <button
              className="back-arrow !p-2"
              onClick={(e) => {
                e.preventDefault();
                parentNavigate("/dashboard");
              }}
            >
              <ArrowLeft />
            </button>
            <div>
              <Breadcrumbs
                crumbs={[
                  { name: t("lang_dashboard"), path: `/dashboard` },
                  { name: t("lang_faqs"), path: "/faqs" },
                ]}
              />
              <h4 className="text-xl md:text-2xl font-semibold text-primary">{t("lang_faqs")}</h4>
            </div>
          </div>
          <div></div>
          <div className="flex items-center">
            <ButtonComponent
              variant="primary"
              icon={<PlusCircle className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />}
              onClick={() => parentNavigate("/faqs/add")}
            >
              {t("lang_Add_New_FAQ")}
            </ButtonComponent>
          </div>
        </div>
        <div className="flex justify-between w-full space-x-4">
          <div className="w-full space-y-3">
            <div className="flex w-full h-full overflow-hidden relative min-w-0">
              <div className="w-full">
                <div className="w-full transition-all duration-100 mb-[10px]">
                  <CommonTable
                    tableId="faqs"
                    columns={columns}
                    data={tableData?.records || []}
                    tableHeader={tableHeaderFilter}
                    onSort={SortData}
                    sortBy={sortBy}
                    orderBy={orderBy}
                    tableFooter={
                      tableData?.records?.length ? (
                        <PaginationComponent
                          count={tableData?.totalPages || 1}
                          page={Page}
                          onChange={(e, v) => setPage(v)}
                          totalRecords={tableData?.totalRecords}
                          startRecord={startRecord}
                          endRecord={endRecord}
                          limit={limit}
                          recordsLabel="Records"
                        />
                      ) : null
                    }
                    loading={dataLoading}
                    skeletonRows={10}
                    emptyMessage={<NoRecords />}
                    rowClick={(row) => Handleedit(row)}
                    rowClassName={(row) =>
                      `select-none ${selectedRowsId?.includes(row?._id) ? "selected-row" : ""}`
                    }
                    containerClassName="faq-table sticky-table module-table overflow-x-auto border border-[var(--border-color)] rounded-xl shadow-[var(--shadow-card)]"
                    NEWAPI={handleTableApi}
                    tableDefaults={defaultCustomFields?.defaultFaqField}
                    tableWiseAllowedSortKeys={defaultCustomFields?.FAQ_SORT_KEYS}
                    userState={userContext}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {openBulkEditModal && (
        <Dialog
          open={openBulkEditModal}
          disableEscapeKeyDown={true}
          onClose={(event, reason) => {
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
              return;
            }
            setOpenBulkEditModal(false);
          }}
          fullScreen
          sx={{
            zIndex: 999999,
            "& .MuiDialog-paper": {
              zIndex: 999999,
              margin: 0,
              width: "100vw",
              height: "100vh",
              maxWidth: "100vw",
              maxHeight: "100vh",
              borderRadius: 0,
            },
          }}
        >
          <BulkEditFaq
            isBulkEdit={true}
            bulkFaqIds={selectedRowsId}
            onClose={() => {
              setOpenBulkEditModal(false);
              setSelectedRowsId([]);
            }}
            onSuccess={(updatedCount) => {
              setOpenBulkEditModal(false);
              setSelectedRowsId([]);
              setIsNotification(true);
              const count = typeof updatedCount === "number" ? updatedCount : 1;
              setNotificationMsg(
                `${count} FAQ${count > 1 ? "s" : ""} updated successfully!`
              );
              setSeverity("success");
              GetAllFaqs();
            }}
          />
        </Dialog>
      )}
    </>
  );
}

export default Faqs;
